// Accessible PrimeReact DataTables (v1.3 ticket 13: RCMC-10 table structure and names, RCMC-11 filter names).
//
// PrimeReact 10.6.2's DataTable falls short in ways its pass-through options (`pt`) can correct without forking the
// library or upgrading it (SPEC "Dependencies"). Each correction is pinned by its own seam-1 test
// (__tests__/RCMC-10-data-tables, RCMC-11-table-filters), so a library upgrade cannot undo one silently:
//   - the table has no name                      → aria-labelledby the page's h1 (or a label, for a table in a dialog);
//   - the body is role=" rowgroup"               → "rowgroup" (the leading space made it an invalid role token);
//   - header cells have no scope                 → scope="col";
//   - every body row and cell gets an onClick    → removed. React gives any element with an onClick an `onclick`
//     handler, and Chrome exposes such an element as clickable: NVDA said "clickable" on every row and cell. The
//     handlers only serve row selection and cell editing, which no table here uses. (If a table ever turns either
//     on, it needs its own options without these two.) An `undefined` value replaces PrimeReact's handler in its
//     prop merge; a function would be chained to it instead;
//   - a sortable header is named by its label only → "<Column>, sortable" (aria-sort still gives the direction, and
//     the visible label stays the start of the name: 2.5.3 label in name);
//   - a sortable header is a Tab stop, but Enter and Space never sort it → they click it (see sortOnEnterOrSpace);
//   - every filter button is "Show Filter Menu"  → "Filter by <Column>", and it says it opens a dialog (it said
//     "true", which means a menu); the popup (a dialog) and its search box were unnamed → "Filter by <Column>" /
//     "Search <Column>"; its list of values is named "<Column>";
//   - the arrow keys in that list move only a highlight → the list points at the highlighted value with
//     aria-activedescendant, so a screen reader follows it (ColumnFilterList; not a pass-through option);
//   - with nothing to list, its "No results found" / "No available options" line is a bare <li> inside the listbox
//     (axe: not an allowed child; a screen reader hears nothing) → an unavailable option.
//
// Ticket 16 (RCMC-04, reflow at 320 CSS px): the table's wrapper, which already scrolls (the library's
// `overflow: auto`), becomes the table's own region, named as the table is. The page never scrolls sideways; a table
// too wide for the window scrolls inside its region, the 1.4.10 exception for data tables. While the region scrolls —
// sideways, or (ticket 22, owner ruling Q37) up and down, as a long table does at desktop width — it is a Tab stop
// (useTableScrollRegion), so the keyboard can scroll it too. Tables have no fixed minimum width any more (v1.2's 50rem
// made every table page 830px wide at 320).
//
// Usage — the DataTable takes the table's options, each Column its own props, a filter popup this file's list:
//   const dt = useRef(null);
//   useTableScrollRegion(dt);
//   <DataTable ref={dt} value={rows} pt={tablePassThrough()} …>
//     <Column field="created_on" {...columnProps("Date", { sortable: true, filter: true })}
//             filterElement={(options) => <ColumnFilterList column="Date" … />} />
//     <Column field="document_size" header="Size" />            // a column that neither sorts nor filters
import { useEffect, useRef } from "react";
import { ListBox } from "primereact/listbox";
import { PAGE_HEADING_ID } from "./PageHeading";

/**
 * Pass-through options for a DataTable.
 * @param {object} [name]
 * @param {string} [name.label]  the table's name, for a table with no page heading to point at (one in a dialog);
 *   by default the table is named by the page's h1 (Util/PageHeading), so the two can never drift apart
 */
export function tablePassThrough({ label } = {}) {
  const name = label ? { "aria-label": label } : { "aria-labelledby": PAGE_HEADING_ID };
  return {
    // Ticket 16 (RCMC-04): the scroll region around the table, named as the table is
    wrapper: { role: "region", ...name },
    table: name,
    tbody: { role: "rowgroup" },
    bodyRow: { onClick: undefined },
    column: {
      headerCell: { scope: "col", onKeyDown: sortOnEnterOrSpace },
      bodyCell: { onClick: undefined },
    },
  };
}

/** True while `region` has more content than it shows on `axis` ("x" | "y") and can scroll that way: an axis it hides
 *  (`overflow-x: hidden`, as the comment list's) never scrolls, however wide its content */
function scrollsOn(region, axis) {
  const overflowing = axis === "x" ? region.scrollWidth > region.clientWidth : region.scrollHeight > region.clientHeight;
  if (!overflowing) return false;
  const overflow = window.getComputedStyle(region)[axis === "x" ? "overflowX" : "overflowY"];
  return !/hidden|clip/.test(overflow);
}

/**
 * A scroll region is a Tab stop exactly while it scrolls, on either axis (ticket 16 for a table at 320 CSS px; ticket 22,
 * owner ruling Q37, for a long table at desktop width and for a Service Request's comment list): the keyboard can then
 * reach it and scroll it with the arrow keys. While it fits it is not — an extra Tab stop that scrolls nothing would only
 * be noise. Re-measured whenever the region or what is inside it changes size (a zoom, a resize, new rows or comments);
 * without ResizeObserver (jsdom), on the window's resize event alone. The attribute is set on the DOM (React does not
 * manage it on the library's wrapper), so a re-render keeps it.
 * @param {Element} region      the scrolling element (role="region", named)
 * @param {Element[]} contents  what inside it can change size
 * @returns {() => void} stops watching (an effect's clean-up)
 */
export function tabStopWhileScrolling(region, contents) {
  const update = () => {
    if (scrollsOn(region, "x") || scrollsOn(region, "y")) region.setAttribute("tabindex", "0");
    else region.removeAttribute("tabindex");
  };
  update();
  const observer = typeof ResizeObserver === "function" ? new ResizeObserver(update) : null;
  if (observer) [region, ...contents].forEach((element) => observer.observe(element));
  window.addEventListener("resize", update);
  return () => {
    observer?.disconnect();
    window.removeEventListener("resize", update);
  };
}

/**
 * Ticket 16 (RCMC-04): the table's scroll region (tablePassThrough's `wrapper`) is a Tab stop while it scrolls
 * (tabStopWhileScrolling) — the table wider than it at 320 CSS px or 400% zoom, or (ticket 22) taller than it at desktop
 * width — and a control the keyboard reaches inside it is brought fully into view.
 * @param {React.RefObject} tableRef  the DataTable's ref
 */
export function useTableScrollRegion(tableRef) {
  useEffect(() => {
    const region = tableRef.current?.getElement()?.querySelector('[data-pc-section="wrapper"]');
    if (!region) return undefined;
    // The region's own size (window, zoom) and the table's (rows, column content)
    const table = region.querySelector("table");
    const stopWatching = tabStopWhileScrolling(region, table ? [table] : []);
    // A control inside the region that the keyboard reaches is brought fully into view. Chrome leaves a control that is
    // already partly in view where it is: at 320 the Date header, left of a region scrolled to the right, showed a
    // sliver and its focus ring was cut on three sides (seam 2). Only what is needed ("nearest"); its focus ring's room
    // at the region's edge comes from portal-tokens.css (scroll-margin). Keyboard focus only (:focus-visible): a click
    // has already put the control where the mouse is. Where :focus-visible is unknown (jsdom), any focus.
    const byKeyboard = (control) => {
      try {
        return control.matches(":focus-visible");
      } catch (unknownSelector) {
        return true;
      }
    };
    const reveal = (event) => {
      const control = event.target;
      if (control !== region && typeof control.scrollIntoView === "function" && byKeyboard(control)) control.scrollIntoView({ block: "nearest", inline: "nearest" });
    };
    region.addEventListener("focusin", reveal);
    return () => {
      stopWatching();
      region.removeEventListener("focusin", reveal);
    };
  }, [tableRef]);
}

// PrimeReact 10.6.2 bug: a sortable header's own Enter / Space handler checks
// `DomHandler.getAttribute(header, 'data-p-sortable-column') === 'true'`, but getAttribute returns the boolean true, so
// the check always fails: the keyboard reaches the sort control and cannot use it. Click the header for it, exactly as
// a mouse user does. A pass-through function runs after the library's own handler (chained, not replaced). The
// library calls preventDefault only when it has sorted, so once a later version fixes its check this stands aside.
function sortOnEnterOrSpace(event) {
  const header = event.currentTarget;
  // Only a key pressed on the header itself (not one bubbling up from its filter button), and only on one that sorts
  if (event.target !== header || header.getAttribute("data-p-sortable-column") !== "true") return;
  // Already handled (by the library, once fixed), or a held key repeating: one press, one sort
  if (event.isDefaultPrevented() || event.repeat) return;
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault(); // Space would scroll the page
    header.click();
  }
}

/**
 * Props for a Column: its visible header, and the names that go with sorting and filtering.
 * @param {string} header  the visible column header
 * @param {object} [options]
 * @param {boolean} [options.sortable]  sorts on click / Enter: named "<header>, sortable"
 * @param {boolean} [options.filter]    has a filter popup: button and popup named "Filter by <header>" (its
 *   filterElement should be a ColumnFilterList for the same column)
 */
export function columnProps(header, { sortable = false, filter = false } = {}) {
  const pt = {};
  if (sortable) pt.headerCell = { "aria-label": `${header}, sortable` };
  if (filter) {
    pt.filterMenuButton = { "aria-label": `Filter by ${header}`, "aria-haspopup": "dialog" };
    pt.filterOverlay = { "aria-label": `Filter by ${header}` };
  }
  return { header, sortable, filter, pt };
}

/**
 * The list of values in a column's filter popup: a PrimeReact ListBox with a search box, named for the column (search
 * box "Search <column>", list "<column>"). When there is nothing to list, the library's message line is made an
 * unavailable option, the one child a listbox may have, so it is read out and axe passes.
 *
 * PrimeReact 10.6.2 bug: while the list has focus, the arrow keys move a highlight (the option's `p-focus` class) but
 * the list never says which option that is — it has no aria-activedescendant — so a screen reader announces nothing
 * as the Investor moves through the values. Its pass-through options cannot help (they never see that state), so this
 * watches the options and points aria-activedescendant at the highlighted one. React does not manage that attribute
 * on the list, so a re-render leaves it alone. If a later version sets it itself, both would agree.
 * @param {object} props
 * @param {string} props.column  the visible column header
 * Any other prop goes to the ListBox (value, options, onChange, …).
 */
export function ColumnFilterList({ column, ...props }) {
  const listBox = useRef(null);

  useEffect(() => {
    const list = listBox.current?.getElement()?.querySelector('[role="listbox"]');
    if (!list) return undefined;
    const pointAtHighlight = () => {
      const highlighted = list.querySelector('[role="option"].p-focus');
      if (highlighted) list.setAttribute("aria-activedescendant", highlighted.id);
      else list.removeAttribute("aria-activedescendant");
    };
    // The highlight moves by class; searching replaces the options themselves
    const observer = new MutationObserver(pointAtHighlight);
    observer.observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ["class"] });
    pointAtHighlight();
    return () => observer.disconnect();
  }, []);

  return (
    <ListBox
      ref={listBox}
      {...props}
      filterInputProps={{ "aria-label": `Search ${column}` }}
      pt={{ list: { "aria-label": column }, emptyMessage: { role: "option", "aria-disabled": "true" } }}
    />
  );
}
